"use client"

import styled from "@emotion/styled"
import { Button } from "@mui/material"
import { LocalizationProvider } from "@mui/x-date-pickers"
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs"
import { HostServiceResponse } from "api"
import dayjs, { Dayjs } from "dayjs"
import "dayjs/locale/he"
import { useTranslations } from "next-intl"
import { useRouter } from "next/navigation"
import { useCallback, useEffect, useMemo, useState } from "react"
import { Calendar3Icon, PriceIcon, XIcon } from "../icons"
import {
  CalendarWithTimeLayout,
  StrikethroughPickersDay,
  StyledDatePicker,
  TimePickerContext,
  datePickerSlotProps,
} from "../PortFacilitiesInnerPage/CalendarWithTimeLayout"
import { formatDurationHebrew } from "../PortFacilitiesInnerPage/constants"
import { StyledClearIcon, StyledFieldIcon } from "../PortFacilitiesInnerPage/shared"
import ExtrasDropdown from "./ExtrasDropdown"

const StyledCard = styled.div`
  width: 375px;
  max-height: 600px;
  border-radius: 12px;
  background: ${({ theme }) => theme.colors.white};
  box-shadow: 0px 0px 17.3px 0px rgba(114, 111, 111, 0.3);
  overflow: hidden;
  position: relative;
  display: flex;
  flex-direction: column;
`

const StyledCardContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 24px 20px;
  flex: 1;
`

const StyledCardTitle = styled.h3`
  margin: 0;
  font-size: 24px;
  font-weight: 700;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.port.defaultTextColor};
  text-align: center;
  text-transform: capitalize;
  width: 100%;
`

const StyledFields = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const StyledFieldLabel = styled.span`
  font-size: 16px;
  font-weight: 300;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.port.defaultTextColor};
`

const StyledFieldWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
`

const StyledStickyFooter = styled.div`
  padding: 24px 24px 16px;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 1) 10%);
  margin-top: auto;
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const StyledPriceRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  span {
    font-size: 18px;
    font-weight: 700;
    color: ${({ theme }) => theme.colors.port.defaultTextColor};
  }

  svg {
    width: 20px;
    height: 20px;
    flex-shrink: 0;
  }
`

const StyledCTAButton = styled(Button)<{ $active: boolean }>`
  && {
    width: 100%;
    height: 46px;
    border-radius: 45px;
    font-size: 18px;
    font-weight: 700;
    text-transform: none;
    text-decoration: none;
    box-shadow: none;
    background-color: ${({ $active, theme }) =>
      $active ? theme.colors.propBlue : theme.colors.port.portBulletColor};
    color: ${({ theme }) => theme.colors.white};
    cursor: ${({ $active }) => ($active ? "pointer" : "default")};

    &:hover {
      background-color: ${({ $active, theme }) =>
        $active ? theme.colors.buttonHoverBackgroundColor : theme.colors.port.portBulletColor};
      box-shadow: none;
      text-decoration: none;
    }
  }
`

type BookingSearchParams = {
  date?: string
  startTime?: string
  endTime?: string
  numberOfPeople?: string
}

/* eslint-disable no-unused-vars */
type BookingCardProps = {
  price: string
  searchParams?: BookingSearchParams
  extras?: HostServiceResponse[]
  busySlots?: string[]
  selectedExtras?: Record<number, number>
  onDateChange?: (date: string) => void
  onStartTimeChange?: (time: string) => void
  onEndTimeChange?: (time: string) => void
  onExtrasChange?: (extras: Record<number, number>) => void
  priceLoading?: boolean
  hostResourceId?: number
  hostLocationName?: string
  shouldDisableDate?: (date: any) => boolean
  availableTimeSlots?: string[]
}
/* eslint-enable no-unused-vars */

export default function BookingCard({
  price,
  searchParams,
  extras,
  busySlots,
  selectedExtras = {},
  onDateChange,
  onStartTimeChange,
  onEndTimeChange,
  onExtrasChange,
  // eslint-disable-next-line no-unused-vars
  priceLoading,
  hostResourceId,
  hostLocationName,
  shouldDisableDate,
  availableTimeSlots,
}: BookingCardProps) {
  const t = useTranslations("portMeetingRoomProduct")
  const router = useRouter()
  const [date, setDate] = useState(searchParams?.date || "")
  const [startTime, setStartTime] = useState(searchParams?.startTime || "")
  const [endTime, setEndTime] = useState(searchParams?.endTime || "")
  const [datePickerOpen, setDatePickerOpen] = useState(false)

  // Sync local state to parent on every change
  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    onDateChange?.(date)
  }, [date])
  useEffect(() => {
    onStartTimeChange?.(startTime)
  }, [startTime])
  useEffect(() => {
    onEndTimeChange?.(endTime)
  }, [endTime])

  const baseSlots = useMemo(() => availableTimeSlots || [], [availableTimeSlots])

  const busySlotsSet = useMemo(() => new Set(busySlots || []), [busySlots])

  const endTimeSlots = useMemo(() => {
    if (!startTime) return baseSlots
    return baseSlots.filter((slot) => slot > startTime)
  }, [startTime, baseSlots])

  const duration = startTime && endTime ? formatDurationHebrew(startTime, endTime) : ""

  const dateDisplayValue = useMemo(() => {
    if (!date) return ""
    const dateStr = dayjs(date).format("DD.MM.YYYY")
    if (startTime) {
      const timeStr = endTime ? `${startTime}-${endTime}` : startTime
      return `${dateStr} , ${timeStr}`
    }
    return dateStr
  }, [date, startTime, endTime])

  const isActive = !!date && !!startTime && !!endTime

  const handleContinue = useCallback(() => {
    if (!isActive || !hostResourceId) return
    const params = new URLSearchParams()
    params.set("resourceId", String(hostResourceId))
    params.set("date", date)
    params.set("startTime", startTime)
    params.set("endTime", endTime)
    if (hostLocationName) params.set("location", hostLocationName)
    const servicesEntries = Object.entries(selectedExtras).filter(([, qty]) => qty > 0)
    if (servicesEntries.length > 0) {
      params.set(
        "services",
        JSON.stringify(servicesEntries.map(([id, qty]) => ({ id: Number(id), quantity: qty }))),
      )
    }
    router.push(`/booking?${params.toString()}`)
  }, [isActive, hostResourceId, date, startTime, endTime, hostLocationName, selectedExtras, router])

  return (
    <StyledCard>
      <StyledCardContent>
        <StyledCardTitle>{t("bookOnline")}</StyledCardTitle>
        <StyledFields>
          <StyledFieldWrapper>
            <StyledFieldLabel>{t("when")}</StyledFieldLabel>
            <TimePickerContext.Provider
              value={{
                startTime,
                endTime,
                startTimeSlots: baseSlots.slice(0, -1),
                endTimeSlots,
                disabledTimeSlots: busySlotsSet,
                duration,
                onStartTimeChange: (val) => {
                  setStartTime(val)
                  onStartTimeChange?.(val)
                },
                onEndTimeChange: (val) => {
                  setEndTime(val)
                  onEndTimeChange?.(val)
                },
                onConfirm: () => setDatePickerOpen(false),
              }}
            >
              <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="he">
                <div style={{ position: "relative" }}>
                  <StyledDatePicker
                    value={date ? dayjs(date) : null}
                    onChange={(newDate: any) => {
                      const val = newDate ? (newDate as Dayjs).format("YYYY-MM-DD") : ""
                      setDate(val)
                      onDateChange?.(val)
                    }}
                    open={datePickerOpen}
                    onOpen={() => setDatePickerOpen(true)}
                    onClose={() => {}}
                    minDate={dayjs()}
                    shouldDisableDate={shouldDisableDate}
                    format="DD/MM/YYYY"
                    slots={{
                      openPickerIcon: () => null,
                      layout: CalendarWithTimeLayout,
                      day: StrikethroughPickersDay,
                    }}
                    slotProps={{
                      ...datePickerSlotProps,
                      textField: {
                        ...datePickerSlotProps.textField,
                        placeholder: t("selectDate"),
                        onClick: () => setDatePickerOpen(true),
                        ...(dateDisplayValue
                          ? {
                              inputProps: {
                                ...datePickerSlotProps.textField.inputProps,
                                value: dateDisplayValue,
                              },
                            }
                          : {}),
                      },
                      popper: {
                        ...datePickerSlotProps.popper,
                        sx: {
                          ...datePickerSlotProps.popper.sx,
                          "& .MuiPaper-root": {
                            ...datePickerSlotProps.popper.sx["& .MuiPaper-root"],
                            height: "auto !important",
                          },
                        },
                      },
                    }}
                  />
                  {date ? (
                    <StyledClearIcon
                      onMouseDown={(e: any) => {
                        e.stopPropagation()
                        e.preventDefault()
                        setDate("")
                        setStartTime("")
                        setEndTime("")
                      }}
                    >
                      <XIcon color="#0e2846" />
                    </StyledClearIcon>
                  ) : (
                    <StyledFieldIcon>
                      <Calendar3Icon color="#687079" />
                    </StyledFieldIcon>
                  )}
                </div>
              </LocalizationProvider>
            </TimePickerContext.Provider>
          </StyledFieldWrapper>

          {extras && extras.length > 0 && (
            <ExtrasDropdown
              extras={extras}
              selectedExtras={selectedExtras}
              onExtrasChange={(val) => onExtrasChange?.(val)}
              label={t("extras")}
            />
          )}
        </StyledFields>
      </StyledCardContent>

      <StyledStickyFooter>
        {isActive && price && (
          <StyledPriceRow>
            <PriceIcon />
            <span>
              {t("totalPrice")} {price}
            </span>
          </StyledPriceRow>
        )}
        <StyledCTAButton
          $active={isActive}
          disabled={!isActive}
          variant="contained"
          disableElevation
          disableRipple={!isActive}
          onClick={handleContinue}
        >
          {t("continueToPayment")}
        </StyledCTAButton>
      </StyledStickyFooter>
    </StyledCard>
  )
}
